import React, { useState } from "react";
import { 
  useGetServiceOrdersQuery, 
  useCreateServiceOrderMutation, 
  useUpdateServiceOrderMutation,
  useAddServiceOrderPaymentMutation,
  useDeleteServiceOrderMutation,
  useGetCustomersQuery 
} from "../services/api";
import { FiTool, FiDroplet, FiPlus, FiEdit2, FiTrash2, FiDollarSign, FiCheck } from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-ET", { month: "short", day: "numeric", year: "numeric" });
}

export default function ServiceOrdersPage() {
  const { data, isLoading } = useGetServiceOrdersQuery();
  const { data: customersData } = useGetCustomersQuery();
  const [createOrder] = useCreateServiceOrderMutation();
  const [updateOrder] = useUpdateServiceOrderMutation();
  const [addPayment] = useAddServiceOrderPaymentMutation();
  const [deleteOrder] = useDeleteServiceOrderMutation();

  const orders = data?.data || [];
  const customers = customersData?.data || [];

  // Calculate KPIs
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(o => o.status === "pending").length;
  const inProgressOrders = orders.filter(o => o.status === "in_progress").length;
  const outstandingBalance = orders.reduce((sum, o) => sum + Number(o.amount_remaining), 0);

  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [form, setForm] = useState({
    customer_id: "",
    service_type: "fix",
    item_description: "",
    weight_grams: "",
    service_price: "",
    payment_type: "full",
    amount_paid: "",
    payment_method: "cash",
    delivery_date: "",
    notes: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    payment_amount: "",
    payment_method: "cash",
    notes: "",
  });

  const [statusForm, setStatusForm] = useState({
    status: "",
    notes: "",
  });

  const [error, setError] = useState("");

  const openCreateModal = () => {
    setEditingOrder(null);
    setForm({
      customer_id: "",
      service_type: "fix",
      item_description: "",
      weight_grams: "",
      service_price: "",
      payment_type: "full",
      amount_paid: "",
      payment_method: "cash",
      delivery_date: "",
      notes: "",
    });
    setError("");
    setShowModal(true);
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setForm({
      customer_id: order.customer_id,
      service_type: order.service_type,
      item_description: order.item_description,
      weight_grams: order.weight_grams || "",
      service_price: order.service_price,
      payment_type: order.payment_type,
      amount_paid: order.amount_paid,
      payment_method: order.payment_method,
      delivery_date: order.delivery_date ? order.delivery_date.split('T')[0] : "",
      notes: order.notes || "",
    });
    setError("");
    setShowModal(true);
  };

  const openPaymentModal = (order) => {
    setSelectedOrder(order);
    setPaymentForm({
      payment_amount: order.amount_remaining,
      payment_method: "cash",
      notes: "",
    });
    setError("");
    setShowPaymentModal(true);
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setStatusForm({
      status: order.status,
      notes: order.notes || "",
    });
    setError("");
    setShowStatusModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Calculate amount_paid based on payment_type
    const servicePrice = Number(form.service_price);
    let amountPaid = Number(form.amount_paid);

    if (form.payment_type === "full") {
      amountPaid = servicePrice;
    } else if (!amountPaid || amountPaid <= 0) {
      setError("Please enter initial payment amount for half payment");
      return;
    }

    try {
      if (editingOrder) {
        // Update existing order
        await updateOrder({
          id: editingOrder.id,
          customer_id: form.customer_id,
          service_type: form.service_type,
          item_description: form.item_description,
          weight_grams: Number(form.weight_grams) || 0,
          service_price: servicePrice,
          delivery_date: form.delivery_date || null,
          notes: form.notes,
        }).unwrap();
      } else {
        // Create new order
        await createOrder({
          ...form,
          weight_grams: Number(form.weight_grams) || 0,
          service_price: servicePrice,
          amount_paid: amountPaid,
        }).unwrap();
      }
      setShowModal(false);
    } catch (err) {
      setError(err?.data?.message || `Failed to ${editingOrder ? 'update' : 'create'} service order`);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await addPayment({
        id: selectedOrder.id,
        ...paymentForm,
        payment_amount: Number(paymentForm.payment_amount),
      }).unwrap();
      setShowPaymentModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to add payment");
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await updateOrder({
        id: selectedOrder.id,
        ...statusForm,
      }).unwrap();
      setShowStatusModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this service order?")) {
      try {
        await deleteOrder(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || "Failed to delete");
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "badge-warning",
      in_progress: "badge-info",
      completed: "badge-success",
      delivered: "badge-secondary",
    };
    return badges[status] || "badge-secondary";
  };

  const getServiceIcon = (type) => {
    return type === "fix" ? <FiTool /> : <FiDroplet />;
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "200px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Service Orders
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Manage fix and wash service orders
          </p>
        </div>
        <button 
          onClick={openCreateModal}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.95rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
          }}
        >
          <FiPlus /> Add Service
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Orders */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              🛠️
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Orders
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {totalOrders}
          </div>
        </div>

        {/* Pending */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ⏳
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Pending
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {pendingOrders}
          </div>
        </div>

        {/* In Progress */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              🔄
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            In Progress
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {inProgressOrders}
          </div>
        </div>

        {/* Outstanding Balance */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💸
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Outstanding Balance
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(outstandingBalance)}
          </div>
        </div>
      </div>

      {/* Service Orders Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        overflowX: "auto"
      }}>
        <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
          All Service Orders
        </h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ 
            width: "100%", 
            minWidth: "1400px",
            borderCollapse: "collapse",
            fontSize: "0.9rem"
          }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "140px" }}>Order #</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "120px" }}>Date</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "180px" }}>Customer</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "120px" }}>Service</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "220px" }}>Item</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "130px" }}>Price</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "130px" }}>Paid</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "130px" }}>Remaining</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "140px" }}>Status</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "120px" }}>Delivery</th>
                <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem", minWidth: "220px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
                    <FiTool size={48} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
                    <div>No service orders yet</div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} style={{ 
                    borderBottom: "1px solid #F1F5F9",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "white";
                  }}
                  >
                    <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>{order.order_number}</td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{formatDate(order.created_at)}</td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{order.customer_name}</td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: order.service_type === "fix" ? "#DBEAFE" : "#E0E7FF",
                        color: order.service_type === "fix" ? "#1E40AF" : "#5B21B6",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.25rem"
                      }}>
                        {getServiceIcon(order.service_type)} {order.service_type.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {order.item_description}
                    </td>
                    <td style={{ padding: "1rem 0.75rem", fontWeight: 700, color: "#1E293B" }}>{formatCurrency(order.service_price)}</td>
                    <td style={{ padding: "1rem 0.75rem", color: "#10B981", fontWeight: 600 }}>{formatCurrency(order.amount_paid)}</td>
                    <td style={{ padding: "1rem 0.75rem", color: Number(order.amount_remaining) > 0 ? "#EF4444" : "#64748B", fontWeight: 600 }}>
                      {formatCurrency(order.amount_remaining)}
                    </td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: 
                          order.status === "completed" ? "#D1FAE5" :
                          order.status === "in_progress" ? "#E0E7FF" :
                          order.status === "delivered" ? "#F1F5F9" :
                          "#FEF3C7",
                        color: 
                          order.status === "completed" ? "#065F46" :
                          order.status === "in_progress" ? "#5B21B6" :
                          order.status === "delivered" ? "#64748B" :
                          "#92400E"
                      }}>
                        {order.status.replace("_", " ")}
                      </span>
                    </td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{formatDate(order.delivery_date)}</td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "nowrap" }}>
                        <button 
                          onClick={() => openEditModal(order)}
                          title="Edit Order"
                          style={{
                            padding: "0.5rem",
                            background: "transparent",
                            border: "1px solid #E2E8F0",
                            borderRadius: "6px",
                            color: "#3B82F6",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#F8FAFC";
                            e.currentTarget.style.borderColor = "#3B82F6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#E2E8F0";
                          }}
                        >
                          <FiEdit2 size={14} />
                        </button>
                        {Number(order.amount_remaining) > 0 && (
                          <button 
                            onClick={() => openPaymentModal(order)}
                            title="Add Payment"
                            style={{
                              padding: "0.5rem 0.65rem",
                              background: "transparent",
                              border: "1px solid #E2E8F0",
                              borderRadius: "6px",
                              color: "#10B981",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.35rem",
                              transition: "all 0.2s",
                              whiteSpace: "nowrap"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#F0FDF4";
                              e.currentTarget.style.borderColor = "#10B981";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.borderColor = "#E2E8F0";
                            }}
                          >
                            <FiDollarSign size={14} /> Pay
                          </button>
                        )}
                        <button 
                          onClick={() => openStatusModal(order)}
                          title="Update Status"
                          style={{
                            padding: "0.5rem 0.65rem",
                            background: "transparent",
                            border: "1px solid #E2E8F0",
                            borderRadius: "6px",
                            color: "#8B5CF6",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            transition: "all 0.2s",
                            whiteSpace: "nowrap"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#F5F3FF";
                            e.currentTarget.style.borderColor = "#8B5CF6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#E2E8F0";
                          }}
                        >
                          <FiCheck size={14} /> Status
                        </button>
                        <button 
                          onClick={() => handleDelete(order.id)}
                          title="Delete"
                          style={{
                            padding: "0.5rem",
                            background: "transparent",
                            border: "1px solid #E2E8F0",
                            borderRadius: "6px",
                            color: "#EF4444",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#FEF2F2";
                            e.currentTarget.style.borderColor = "#EF4444";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#E2E8F0";
                          }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: "600px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingOrder ? 'Edit Service Order' : 'New Service Order'}</h3>
              <button className="btn-ghost" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Customer *</label>
                  <select 
                    className="form-select" 
                    value={form.customer_id} 
                    onChange={(e) => setForm({ ...form, customer_id: e.target.value })} 
                    required
                    disabled={editingOrder}
                  >
                    <option value="">Select customer...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} - {c.phone}</option>
                    ))}
                  </select>
                  {editingOrder && (
                    <small style={{ color: "var(--color-text-secondary)" }}>
                      Customer cannot be changed after order creation
                    </small>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Service Type *</label>
                    <select 
                      className="form-select" 
                      value={form.service_type} 
                      onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                    >
                      <option value="fix">Fix (Repair)</option>
                      <option value="wash">Wash (Cleaning)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Weight (grams)</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={form.weight_grams} 
                      onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} 
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Item Description *</label>
                  <textarea 
                    className="form-textarea" 
                    value={form.item_description} 
                    onChange={(e) => setForm({ ...form, item_description: e.target.value })} 
                    rows="2"
                    placeholder="e.g., Gold ring - broken prong"
                    required
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Service Price (ETB) *</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={form.service_price} 
                      onChange={(e) => setForm({ ...form, service_price: e.target.value })} 
                      step="0.01"
                      min="0"
                      required
                      disabled={editingOrder}
                    />
                    {editingOrder && (
                      <small style={{ color: "var(--color-text-secondary)" }}>
                        Price cannot be changed after order creation
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Payment Type *</label>
                    <select 
                      className="form-select" 
                      value={form.payment_type} 
                      onChange={(e) => setForm({ ...form, payment_type: e.target.value })}
                      disabled={editingOrder}
                    >
                      <option value="full">Full Payment</option>
                      <option value="half">Half Payment</option>
                    </select>
                    {editingOrder && (
                      <small style={{ color: "var(--color-text-secondary)" }}>
                        Payment type cannot be changed
                      </small>
                    )}
                  </div>
                </div>

                {form.payment_type === "half" && !editingOrder && (
                  <div className="form-group">
                    <label className="form-label">Initial Payment Amount (ETB) *</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={form.amount_paid} 
                      onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} 
                      step="0.01"
                      min="0"
                      placeholder={`e.g., ${Number(form.service_price) / 2}`}
                      required={form.payment_type === "half"}
                    />
                    <small style={{ color: "var(--color-text-secondary)" }}>
                      Remaining: {formatCurrency(Number(form.service_price) - Number(form.amount_paid || 0))}
                    </small>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Payment Method *</label>
                    <select 
                      className="form-select" 
                      value={form.payment_method} 
                      onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                      disabled={editingOrder}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                    {editingOrder && (
                      <small style={{ color: "var(--color-text-secondary)" }}>
                        Payment method cannot be changed
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Delivery Date</label>
                    <input 
                      type="date" 
                      className="form-input" 
                      value={form.delivery_date} 
                      onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea 
                    className="form-textarea" 
                    value={form.notes} 
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} 
                    rows="2"
                  />
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {editingOrder ? 'Update Order' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Payment - {selectedOrder.order_number}</h3>
              <button className="btn-ghost" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddPayment}>
              <div className="modal-body">
                <div style={{ 
                  padding: "var(--space-3)", 
                  background: "var(--color-surface-elevated)", 
                  borderRadius: "var(--border-radius-sm)",
                  marginBottom: "var(--space-4)"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--space-2)" }}>
                    <span>Service Price:</span>
                    <strong>{formatCurrency(selectedOrder.service_price)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--space-2)" }}>
                    <span>Already Paid:</span>
                    <strong style={{ color: "var(--color-success)" }}>{formatCurrency(selectedOrder.amount_paid)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "var(--space-2)", borderTop: "1px solid var(--color-border)" }}>
                    <span style={{ fontWeight: 600 }}>Remaining:</span>
                    <strong style={{ color: "var(--color-danger)", fontSize: "1.25rem" }}>
                      {formatCurrency(selectedOrder.amount_remaining)}
                    </strong>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Amount (ETB) *</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    value={paymentForm.payment_amount} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_amount: e.target.value })} 
                    step="0.01"
                    min="0"
                    max={selectedOrder.amount_remaining}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Method *</label>
                  <select 
                    className="form-select" 
                    value={paymentForm.payment_method} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea 
                    className="form-textarea" 
                    value={paymentForm.notes} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} 
                    rows="2"
                  />
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Add Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Update Status - {selectedOrder.order_number}</h3>
              <button className="btn-ghost" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateStatus}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select 
                    className="form-select" 
                    value={statusForm.status} 
                    onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                    required
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea 
                    className="form-textarea" 
                    value={statusForm.notes} 
                    onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })} 
                    rows="3"
                  />
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowStatusModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Update Status</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
